import React, { useEffect, useMemo, useState } from 'react';
import StopIcon from '../../../../assets/images/standard/stop.svg';
import Next from '../../../../assets/images/standard/arrow-right.svg';
import NextInactive from '../../../../assets/images/standard/arrow-right-inactive.svg';
import Previous from '../../../../assets/images/standard/arrow-left.svg';
import PreviousInactive from '../../../../assets/images/standard/arrow-left-inactive.svg';
import { convertSecondsToDisplayTime } from '../../../../utils/convertSecondsToDisplayTime';
import { BlockType } from '../../../../types/Study';
import { ArrowRight, Flag, MoreVertical, Pause, X } from 'react-feather';
import {
  hapticsImpactLight,
  hapticsImpactMedium,
} from '../../../../utils/haptics';
import { closeSnackbar } from 'notistack';
import QBankDonut from '../../../Misc/QBankDonut';
import posthog from 'posthog-js';
import { Intercom } from '../../../../assets/svgs/Intercom';
import Tooltip from '@mui/material/Tooltip';
import {
  selectStudyPhase,
  StudyPhase,
} from '../../../../slices/study/studySlice';
import { useStudyPhaseSend } from '../../../../studyPhase';
import { useSelector } from 'react-redux';
import { selectActiveBlock } from '../../../../slices/blocks/blocksSlice';
import { selectAccessLevel } from '../../../../slices/subscriptions/subscriptionsSlice';
import { getAccessLimits } from '../../../../utils/subAccessUtils';
import { useAccess } from '../../../../hooks/useAccess';
import { selectTodaySession } from '../../../../slices/todaySession/todaySessionSlice';
import { Capacitor } from '@capacitor/core';
import PopoverPauseEndMenu from '../PopoverPauseEndMenu';

interface FooterProps {
  activeBlockId: string;
  blockType: BlockType;
  isSubmitted: boolean;
  timeRemaining: number;
  activeVignetteId: string;
  pauseBlock: () => void;
  setConfirmEndIsOpen: (value: boolean) => void;
  nextQuestion: () => void;
  prevQuestion: () => void;
  currentQuestion: number;
  totalQuestions: number;
  isDemo?: boolean;
  toggleReport: () => void;
  allDemoQuestionsSubmitted?: boolean;
  createAccountModalIsOpen?: boolean;
  functionMenuIsOpen?: boolean;
  setFunctionMenuIsOpen: (value: boolean) => void;
  refreshFlashcards: () => void;
  renderSwitchPhaseButton: () => React.ReactNode;
  isTryAgainMode: boolean;
  setIsTryAgainMode: (value: boolean) => void;
  setUpgradeModalIsOpen: (value: boolean) => void;
  tutorModeSubmitButton: () => React.ReactNode;
  shouldRenderAnswerAndExplanation: () => boolean;
}

const Footer: React.FC<FooterProps> = ({
  activeBlockId,
  blockType,
  isSubmitted,
  // changePhase,
  timeRemaining,
  activeVignetteId,
  pauseBlock,
  setConfirmEndIsOpen,
  nextQuestion,
  prevQuestion,
  currentQuestion,
  totalQuestions,
  isDemo,
  toggleReport,
  allDemoQuestionsSubmitted,
  createAccountModalIsOpen,
  functionMenuIsOpen,
  setFunctionMenuIsOpen,
  refreshFlashcards,
  renderSwitchPhaseButton,
  isTryAgainMode,
  setIsTryAgainMode,
  setUpgradeModalIsOpen,
  tutorModeSubmitButton,
  shouldRenderAnswerAndExplanation,
}) => {
  const activeBlock = useSelector(selectActiveBlock);
  const studyPhase = useSelector(selectStudyPhase);
  const { sendPhaseEvent } = useStudyPhaseSend();
  const accessLevel = useSelector(selectAccessLevel);
  const qbankLimit = getAccessLimits(accessLevel)?.qbankDaily;
  const todaySession = useSelector(selectTodaySession);
  const { isGated } = useAccess();

  const [tutorModeQuestionTimer, setTutorModeQuestionTimer] = useState(0);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (blockType === BlockType.tutor) {
      setTutorModeQuestionTimer(0);
    }
  }, [activeVignetteId, blockType]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (blockType === BlockType.tutor && isTabVisible) {
      timer = setInterval(() => {
        setTutorModeQuestionTimer((prevTime) => prevTime + 1);
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [blockType, isTabVisible]);

  const calculateStudySessionStats = () => {
    if (!activeBlock) return [];
    const correct =
      (activeBlock.correctCount / activeBlock.vignetteCount) * 100;
    const incorrect =
      (activeBlock.incorrectCount / activeBlock.vignetteCount) * 100;
    const omitted =
      (activeBlock.omittedCount / activeBlock.vignetteCount) * 100;
    const total = [
      { value: correct, label: 'Correct' },
      { value: incorrect, label: 'Incorrect' },
      { value: omitted, label: 'Omitted' },
    ];
    return total
      .filter((stat) =>
        ['Right', 'Wrong', 'Omitted', 'Correct', 'Incorrect'].includes(
          stat.label
        )
      )
      .map((stat) => ({
        ...stat,
        value:
          typeof stat.value === 'string' ? parseInt(stat.value) : stat.value,
      }));
  };

  const renderPerformanceDonut = useMemo(() => {
    if (blockType === BlockType.tutor) return null;
    if (!isSubmitted) return null;
    const stats = calculateStudySessionStats();
    const percentage = stats.find((stat) => stat.label === 'Correct')?.value;
    return (
      <QBankDonut
        percentage={percentage ? Math.round(percentage)?.toString() : '0'}
        data={stats}
      />
    );
  }, [isSubmitted, blockType, activeBlockId]);

  const renderTimeDisplay = () => {
    if (studyPhase === StudyPhase.BLOCK_REVIEW || isSubmitted) return null;
    return (
      <div className="time-display">
        {blockType === BlockType.tutor && (
          <div className="m-b-0-25" style={{ fontSize: '1.5rem' }}>
            {convertSecondsToDisplayTime(tutorModeQuestionTimer)}
          </div>
        )}
        {blockType === BlockType.timed ? 'Block Time Remaining: ' : ''}
        {convertSecondsToDisplayTime(timeRemaining)}
        {blockType === BlockType.tutor ? ' Overall' : ''}
      </div>
    );
  };

  const handleEndReview = () => {
    setConfirmEndIsOpen(false);
    hapticsImpactMedium();
    closeSnackbar();
    refreshFlashcards();
    sendPhaseEvent({ type: 'END_QBANK_REVIEW' });
  };

  const renderPreviousQuestionButton = () => {
    if (isDemo && allDemoQuestionsSubmitted) return null;
    return (
      <button
        className={`button button--exam-glass button--change-question ${
          !shouldRenderAnswerAndExplanation() &&
          activeBlock?.type === BlockType.tutor
            ? 'button--change-question--pre-submit'
            : ''
        }`}
        onClick={() => {
          prevQuestion();
          hapticsImpactLight();
        }}
      >
        {currentQuestion === 0 ? (
          <img src={PreviousInactive} alt="Previous Question" />
        ) : (
          <img src={Previous} alt="Previous Question" />
        )}
      </button>
    );
  };

  const renderNextQuestionButton = () => {
    if (isDemo && allDemoQuestionsSubmitted) return null;
    const isNextQuestionBeyondLimit =
      isGated() &&
      qbankLimit !== undefined &&
      !todaySession?.isFirstSession &&
      currentQuestion + 1 >= qbankLimit;
    return (
      <button
        className={`button button--exam-glass button--change-question ${
          !shouldRenderAnswerAndExplanation() &&
          activeBlock?.type === BlockType.tutor
            ? 'button--change-question--pre-submit'
            : ''
        }`}
        onClick={() => {
          hapticsImpactLight();
          if (isNextQuestionBeyondLimit) {
            setUpgradeModalIsOpen(true);
          } else {
            nextQuestion();
          }
        }}
      >
        {currentQuestion === totalQuestions - 1 &&
        !isNextQuestionBeyondLimit ? (
          <img src={NextInactive} alt="Next Question" />
        ) : (
          <img src={Next} alt="Next Question" />
        )}
      </button>
    );
  };

  const renderEndReviewButton = () => {
    if (!isSubmitted) return null;
    if (isDemo) return null;
    return (
      <button
        className="button button--exam-glass button--end-review"
        onClick={handleEndReview}
      >
        <div>End Review</div>
        <ArrowRight />
      </button>
    );
  };

  const renderCreateAccountButton = () => {
    if (!isDemo) return null;
    return (
      <a
        className={`button button--exam-glass button--create-account ${allDemoQuestionsSubmitted && !createAccountModalIsOpen ? 'pulse' : ''}`}
        href="/auth?signup=true"
        onClick={() => {
          posthog?.capture('ms_demo_create_account', {
            isFooterButton: true,
          });
        }}
      >
        Create Account
      </a>
    );
  };

  const renderDefaultMobileFooter = () => {
    return (
      <>
        <button
          className="button button--exam-glass button--function-menu"
          onClick={() => {
            setFunctionMenuIsOpen(!functionMenuIsOpen);
            hapticsImpactLight();
          }}
        >
          {functionMenuIsOpen ? <X /> : <MoreVertical />}
        </button>
        {renderPreviousQuestionButton()}
        {renderEndReviewButton()}
        {!shouldRenderAnswerAndExplanation() &&
          activeBlock?.type === BlockType.tutor &&
          tutorModeSubmitButton()}
        {renderNextQuestionButton()}
      </>
    );
  };

  const renderDemoMobileFooter = () => {
    return (
      <>
        <div style={{ display: 'flex', gap: '.5rem', width: '100%' }}>
          {renderPreviousQuestionButton()}
          {renderNextQuestionButton()}
        </div>
        {renderCreateAccountButton()}
      </>
    );
  };

  const renderTryAgainButton = () => {
    if (blockType !== BlockType.tutor) return null;
    if (isDemo) return null;
    return (
      <Tooltip
        title="Try Again Mode allows you to submit a second time before seeing the correct answer."
        enterTouchDelay={0}
        enterDelay={250}
      >
        <button
          className="button button--exam-glass try-again-toggle"
          onClick={() => {
            setIsTryAgainMode(!isTryAgainMode);
            hapticsImpactLight();
          }}
        >
          Try Again Mode: {isTryAgainMode ? 'On' : 'Off'}
        </button>
      </Tooltip>
    );
  };

  const renderPauseSubmitButton = () => {
    if (isSubmitted || isDemo) return null;
    if (Capacitor.isNativePlatform()) {
      return (
        <PopoverPauseEndMenu
          isFooter={true}
          allQuestionsSubmitted={false}
          studyPhase={studyPhase}
          isDemo={false}
          isSubmitted={isSubmitted}
          pauseBlock={pauseBlock}
          setConfirmEndIsOpen={setConfirmEndIsOpen}
        />
      );
    } else {
      return (
        <Tooltip
          title={
            <div className="pause-end-tip">
              <button
                className="button button--glass button--end"
                onClick={() => {
                  setConfirmEndIsOpen(true);
                  hapticsImpactLight();
                  closeSnackbar();
                }}
              >
                <img src={StopIcon} alt="End Session" />
                <div>Submit Block</div>
              </button>
              {studyPhase === StudyPhase.BLOCK_STUDY &&
                !isDemo &&
                !isSubmitted && (
                  <button
                    onClick={pauseBlock}
                    className="button button--glass button--pause"
                    style={{ flexDirection: 'row' }}
                  >
                    <Pause />
                    <div>Pause Block</div>
                  </button>
                )}
            </div>
          }
          placement="top"
          arrow
          enterTouchDelay={0}
          enterDelay={0}
          leaveDelay={250}
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: 'var(--surface-modal-glass-light)',
                border: '0',
                padding: '.25rem',
                borderRadius: '.5rem',
                width: 'fit-content',
              },
              '& .MuiTooltip-arrow': {
                color: 'var(--surface-modal-glass-light)',
              },
            },
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                  padding: 8,
                },
              },
            ],
          }}
        >
          <button
            className="button button--end"
            onClick={() => {
              hapticsImpactLight();
              closeSnackbar();
            }}
          >
            <img src={StopIcon} alt="End Session" />
          </button>
        </Tooltip>
      );
    }
  };

  return (
    <div className={`footer ${isSubmitted ? 'is-submitted' : ''}`}>
      {renderTimeDisplay()}
      {renderPerformanceDonut}
      <div className="spacer"></div>
      <div className="footer_right">
        {renderTryAgainButton()}
        <Tooltip title="Help" enterTouchDelay={0}>
          <button
            className="button button--exam-glass button--help"
            onClick={() => {
              window.Intercom('showNewMessage');
            }}
          >
            <Intercom />
          </button>
        </Tooltip>
        {!isDemo && (
          <Tooltip title="Report Question" enterTouchDelay={0}>
            <button
              className="button button--exam-glass button--report"
              onClick={toggleReport}
            >
              <Flag />
            </button>
          </Tooltip>
        )}
        {renderSwitchPhaseButton()}
        {renderCreateAccountButton()}
        {renderEndReviewButton()}
        {renderPauseSubmitButton()}
      </div>
      <div className="footer_mobile" style={isDemo ? { flexWrap: 'wrap' } : {}}>
        {!isDemo ? renderDefaultMobileFooter() : renderDemoMobileFooter()}
      </div>
    </div>
  );
};

export default Footer;
